import { Fragment } from "react";
import {
  Font,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { VendorGroup } from "./VendorCard";
import moment from "moment";

Font.register({
  family: "Roboto",
  fonts: [
    { src: "/fonts/Roboto-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Roboto-Bold.ttf", fontWeight: "bold" },
  ],
});

interface Props {
  vendorGroups: VendorGroup[];
  filters: { eventName?: string; organizerLogoUrl?: string | null };
}

const usd = (cents: number) =>
  `$${(Math.abs(cents) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

const SummaryLine = ({
  label,
  value,
  bold,
  large,
  color,
  indent,
}: {
  label: string;
  value: string;
  bold?: boolean;
  large?: boolean;
  color?: string;
  indent?: boolean;
}) => (
  <View style={s.summaryLine}>
    <Text style={[s.summaryLabel, bold ? s.bold : {}, large ? s.large : {}, indent ? { paddingLeft: 12 } : {}]}>
      {label}
    </Text>
    <Text style={[s.summaryValue, bold ? s.bold : {}, large ? s.large : {}, color ? { color } : {}]}>
      {value}
    </Text>
  </View>
);

const Divider = () => <View style={s.divider} />;

const VendorPage = ({
  group,
  filters,
}: {
  group: VendorGroup;
  filters: Props["filters"];
}) => (
  <Page size="LETTER" style={s.page}>
    {/* Header (repeats on each page if the vendor spans multiple) */}
    <View style={s.header} fixed>
      <Image style={s.logo} src={filters.organizerLogoUrl || "/static/Ronin_RGB_POS.png"} />
      <View style={s.headerRight}>
        <Text style={s.title}>Vendor Payout Statement</Text>
        {filters.eventName && <Text style={s.subtitle}>{filters.eventName}</Text>}
      </View>
    </View>

    {/* Vendor banner (repeats on each page) */}
    <View style={s.vendorBanner} fixed>
      <Text style={s.vendorBannerText}>{group.vendorName}</Text>
      <Text style={s.vendorBannerSub}>
        {group.locations.length} location{group.locations.length !== 1 ? "s" : ""}
        {" \u00b7 "}Commission: {group.revenueShare}%
        {" \u00b7 "}Sales tax: {group.salesTaxResponsible ? "Vendor remits" : "Organizer remits"}
      </Text>
    </View>

    {/* Per-location financial summaries */}
    {group.locations.map((loc, idx) => (
      <View key={idx} style={s.section} wrap={false}>
        <Text style={s.sectionTitle}>{loc.locationName}</Text>
        <SummaryLine label="Gross Sales" value={usd(loc.grossSales)} bold />
        {loc.grossCredit > 0 && <SummaryLine label="Credit Sales" value={usd(loc.grossCredit)} indent />}
        {loc.grossRfid > 0 && <SummaryLine label="RFID Sales" value={usd(loc.grossRfid)} indent />}
        {loc.grossCashBalance > 0 && <SummaryLine label="Cash Balance Sales" value={usd(loc.grossCashBalance)} indent />}
        <SummaryLine label="Less Sales Tax" value={`(${usd(loc.salesTax)})`} />
        <SummaryLine label="Vendor Net Sales" value={usd(loc.netSales)} bold />
        <SummaryLine label={`Commission (${loc.revenueShare}%)`} value={`(${usd(loc.commissionAmount)})`} />
        <SummaryLine label="Vendor Commission" value={usd(loc.vendorCommission)} bold />
      </View>
    ))}

    {/* Deductions (commissary + manual) */}
    {(group.deductionCategories.length > 0 || group.manualDeductions.length > 0) && (
      <View style={s.section} wrap={false}>
        <Text style={s.sectionTitle}>Deductions</Text>
        {group.deductionCategories.map((cat, idx) => (
          <SummaryLine
            key={`cat-${idx}`}
            label={`${cat.categoryName} (${cat.totalCount} items)`}
            value={usd(cat.totalAmount)}
          />
        ))}
        {group.deductionCategories.length > 0 && group.manualDeductions.length > 0 && (
          <SummaryLine label="Commissary subtotal" value={usd(group.commissaryTotal)} />
        )}
        {group.manualDeductions.map((d, idx) => (
          <SummaryLine
            key={`man-${idx}`}
            label={`${d.description || "Deduction"} (${d.created_at ? moment(d.created_at).format("M/D") : ""})`}
            value={usd(d.amount)}
          />
        ))}
        <Divider />
        <SummaryLine label="Total Deductions" value={usd(group.totalDeductions)} bold />
      </View>
    )}

    {/* Adjustments */}
    {group.adjustments.length > 0 && (
      <View style={s.section} wrap={false}>
        <Text style={s.sectionTitle}>Adjustments</Text>
        {group.adjustments.map((adj, idx) => (
          <SummaryLine
            key={idx}
            label={`${adj.description || "Adjustment"} (${adj.created_at ? moment(adj.created_at).format("M/D") : ""})`}
            value={usd(adj.amount)}
          />
        ))}
        <Divider />
        <SummaryLine label="Total Adjustments" value={usd(group.totalAdjustments)} bold />
      </View>
    )}

    {/* Payout */}
    <View style={s.totalDueSection} wrap={false}>
      <SummaryLine label="Vendor Commission" value={usd(group.totalVendorCommission)} />
      {group.totalTips > 0 && (
        <SummaryLine label="Tips" value={usd(group.totalTips)} />
      )}
      {group.salesTaxResponsible && group.totalTax > 0 && (
        <SummaryLine label="Sales Tax (vendor remits)" value={usd(group.totalTax)} />
      )}
      {group.totalAdjustments > 0 && (
        <SummaryLine label="Adjustments" value={usd(group.totalAdjustments)} />
      )}
      {group.totalDeductions > 0 && (
        <SummaryLine label="Less: Deductions" value={`(${usd(group.totalDeductions)})`} />
      )}
      {group.participationFee > 0 && (
        <SummaryLine label="Less: Participation Fee" value={`(${usd(group.participationFee)})`} />
      )}
      <Divider />
      <SummaryLine
        label="Total Due Vendor"
        value={usd(group.totalDue)}
        bold
        large
        color="#15803d"
      />
    </View>

    <Text
      style={s.footer}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      fixed
    />
  </Page>
);

export const VendorPayoutPDF: React.FC<Props> = ({ vendorGroups, filters }) => (
  <Document>
    {vendorGroups.map((group, idx) => (
      <VendorPage key={idx} group={group} filters={filters} />
    ))}
  </Document>
);

const s = StyleSheet.create({
  page: { padding: 28, backgroundColor: "#FFFFFF", fontFamily: "Roboto", fontSize: 9 },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  headerRight: { textAlign: "right" },
  logo: { width: 80, height: "auto" },
  title: { fontSize: 18, fontWeight: "bold", color: "#B71C1C", marginBottom: 3 },
  subtitle: { fontSize: 12, color: "#333", marginBottom: 2 },
  vendorBanner: { backgroundColor: "#B71C1C", padding: 8, borderRadius: 3, marginBottom: 12, flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  vendorBannerText: { fontSize: 14, fontWeight: "bold", color: "#FFFFFF" },
  vendorBannerSub: { fontSize: 10, color: "#FFCDD2" },
  section: { marginBottom: 10, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 2, overflow: "hidden" },
  sectionTitle: { fontSize: 10, fontWeight: "bold", color: "#FFFFFF", backgroundColor: "#B71C1C", paddingVertical: 4, paddingHorizontal: 6 },
  summaryLine: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 3, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: "#E8E8E8" },
  summaryLabel: { fontSize: 9, color: "#333" },
  summaryValue: { fontSize: 9, color: "#333", textAlign: "right" },
  bold: { fontWeight: "bold" },
  large: { fontSize: 13 },
  divider: { borderBottomWidth: 1.5, borderBottomColor: "#999", marginVertical: 2 },
  totalDueSection: { backgroundColor: "#f0fdf4", padding: 10, borderRadius: 3, marginBottom: 16, borderWidth: 1, borderColor: "#bbf7d0" },
  footer: { position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center", fontSize: 7, color: "#999" },
});
